import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import './CustomRequirementsForm.css';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000/api';

function CustomRequirementsForm({ requirements, docType, docVersion, onRuleUpdate, showToast }) {
  const [newRequirement, setNewRequirement] = useState('');
  const [editingIndex, setEditingIndex] = useState(null);
  const [editingText, setEditingText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [expandedTexts, setExpandedTexts] = useState({}); // Track which texts are expanded
  const formRef = useRef(null);
  
  const MAX_PREVIEW_LENGTH = 150; // Characters to show before truncation

  // Smooth scroll to form when adding new requirement
  useEffect(() => {
    if (formRef.current && newRequirement) {
      formRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [newRequirement]);

  const handleAddRequirement = async (e) => {
    e.preventDefault();
    if (!newRequirement.trim()) {
      showToast('Please enter a requirement', 'error');
      return;
    }

    setIsSubmitting(true);
    try {
      const params = new URLSearchParams();
      if (docVersion) {
        params.append('version', docVersion);
      }
      const url = `${API_BASE}/rules/${docType}/custom-requirements${params.toString() ? '?' + params.toString() : ''}`;
      
      await axios.post(url, { text: newRequirement.trim() });
      setNewRequirement('');
      showToast('Custom requirement added successfully', 'success');
      onRuleUpdate();
    } catch (error) {
      console.error('Error adding custom requirement:', error);
      showToast('Failed to add custom requirement', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditStart = (index, text) => {
    setEditingIndex(index);
    setEditingText(text);
  };

  const handleEditCancel = () => {
    setEditingIndex(null);
    setEditingText('');
  };

  const handleEditSave = async (index) => {
    if (!editingText.trim()) {
      showToast('Requirement text cannot be empty', 'error');
      return;
    }

    try {
      const params = new URLSearchParams();
      if (docVersion) {
        params.append('version', docVersion);
      }
      const url = `${API_BASE}/rules/${docType}/custom-requirements/${index}${params.toString() ? '?' + params.toString() : ''}`;
      
      await axios.put(url, { text: editingText.trim() });
      setEditingIndex(null);
      setEditingText('');
      showToast('Custom requirement updated successfully', 'success');
      onRuleUpdate();
    } catch (error) {
      console.error('Error updating custom requirement:', error);
      showToast('Failed to update custom requirement', 'error');
    }
  };

  const handleDelete = async (index) => {
    if (!window.confirm('Are you sure you want to delete this requirement?')) {
      return;
    }

    try {
      const params = new URLSearchParams();
      if (docVersion) {
        params.append('version', docVersion);
      }
      const url = `${API_BASE}/rules/${docType}/custom-requirements/${index}${params.toString() ? '?' + params.toString() : ''}`;
      
      await axios.delete(url);
      showToast('Custom requirement deleted successfully', 'success');
      onRuleUpdate();
    } catch (error) {
      console.error('Error deleting custom requirement:', error);
      showToast('Failed to delete custom requirement', 'error');
    }
  };

  const toggleTextExpansion = (index) => {
    setExpandedTexts(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const getTextPreview = (text, index) => {
    if (text.length <= MAX_PREVIEW_LENGTH) {
      return text;
    }
    return expandedTexts[index] ? text : text.substring(0, MAX_PREVIEW_LENGTH) + '...';
  };

  const requirementsList = requirements || [];

  return (
    <div className="custom-requirements-form">
      <div className="custom-requirements-header">
        <h3>Custom Requirements (TODOs)</h3>
        <p className="form-description">
          Add custom validation requirements in natural language. These will be stored as TODOs in the rules file.
        </p>
      </div>

      {/* Add new requirement form */}
      <form ref={formRef} onSubmit={handleAddRequirement} className="add-requirement-form">
        <div className="form-group">
          <label htmlFor="new-requirement">New Requirement</label>
          <textarea
            id="new-requirement"
            value={newRequirement}
            onChange={(e) => setNewRequirement(e.target.value)}
            placeholder="Enter a custom requirement in natural language (e.g., 'Verify that all signatures are present on page 3')"
            rows={3}
            className="requirement-input"
          />
        </div>
        <button 
          type="submit" 
          className="btn-add-requirement"
          disabled={isSubmitting || !newRequirement.trim()}
        >
          {isSubmitting ? 'Adding...' : 'Add Requirement'}
        </button>
      </form>

      {/* Requirements list */}
      <div className="requirements-list">
        <h4>Existing Requirements ({requirementsList.length})</h4>
        {requirementsList.length === 0 ? (
          <div className="empty-state">
            <p>No custom requirements added yet. Add one above to get started.</p>
          </div>
        ) : (
          <div className="requirements-items">
            {requirementsList.map((requirement, index) => (
              <div key={index} className="requirement-item">
                {editingIndex === index ? (
                  <div className="requirement-edit">
                    <textarea
                      value={editingText}
                      onChange={(e) => setEditingText(e.target.value)}
                      rows={2}
                      className="requirement-edit-input"
                      autoFocus
                    />
                    <div className="requirement-edit-actions">
                      <button
                        onClick={() => handleEditSave(index)}
                        className="btn-save"
                        disabled={!editingText.trim()}
                      >
                        Save
                      </button>
                      <button
                        onClick={handleEditCancel}
                        className="btn-cancel"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="requirement-display">
                    <div className="requirement-content">
                      <span className="requirement-number">#{index + 1}</span>
                      <div className="requirement-text-wrapper">
                        <p className="requirement-text">
                          {getTextPreview(requirement.text, index)}
                        </p>
                        {requirement.text.length > MAX_PREVIEW_LENGTH && (
                          <button
                            onClick={() => toggleTextExpansion(index)}
                            className="btn-expand-text"
                          >
                            {expandedTexts[index] ? 'Show less' : 'Read more'}
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="requirement-actions">
                      <button
                        onClick={() => handleEditStart(index, requirement.text)}
                        className="btn-edit"
                        title="Edit requirement"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(index)}
                        className="btn-delete"
                        title="Delete requirement"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default CustomRequirementsForm;

